import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { Notific } from "src/model/class/general classes/notific";
import {
  NotificationStoreAction,
  StorageNativeStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { LocalStorageService } from "src/service/local-storage.service";
import { NotificationService } from "src/service/notification.service";
import {
  notificationDeleteCompleteAction,
  notificationInsertCompleteAction,
  notificationLoadErrorAction,
  notificationLoadLastModifiedFromApiAction,
  notificationLoadListFromApiAction,
  notificationLoadSuccessAction,
  notificationUnsetKeyStorage,
  notificationUpdateCompleteAction,
} from "../actions/notification-actions";
import { currentUserData } from "../selectors/authentication.selectors";
import { listNotification } from "../selectors/notifications.selectors";

@Injectable()
export class NotificationEffects {
  constructor(
    private _actions$: Actions,
    private _localStorageService: LocalStorageService,
    private _notificationService: NotificationService,
    private _store: Store
  ) { }

  /**
   * Effect who catch main event and unset specific server key storage
   */
  public notificationUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => notificationUnsetKeyStorage())
    )
  );
  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public notificationStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationStoreAction.NOTIFICATION_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listNotification)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const notificationObject: GenericStoreApiData = combinedObject[2];

        if (notificationObject?.data) {
          return notificationLoadLastModifiedFromApiAction({
            notifications: notificationObject.data,
          });
        } else {
          return notificationLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full lsit of notification effect
   */
  public notificationListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationStoreAction.NOTIFICATION_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._notificationService.getNotifications().pipe(
          map((notificationObject: GenericApiData) =>
            notificationLoadSuccessAction({ notificationObject })
          ),
          catchError(() =>
            of(notificationLoadErrorAction({ notificationObject: null }))
          )
        );
      })
    )
  );

  /**
   * Load full lsit of notification effect
   */
  public notificationListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationStoreAction.NOTIFICATION_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._notificationService
          .getNotificationByLastModified(action.notifications)
          .pipe(
            map((notificationObject: GenericApiData) =>
              notificationLoadSuccessAction({ notificationObject })
            ),
            catchError(() =>
              of(notificationLoadErrorAction({ notificationObject: null }))
            )
          );
      })
    )
  );

  /**
   * Notification add effect
   */
  public notificationAddEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationStoreAction.NOTIFICATION_INSERT_NOTIFICATION),
      switchMap((action: any) => {
        return this._notificationService
          .getNotificationByID(action.idNotification)
          .pipe(
            map((newNotification: Notific) =>
              notificationInsertCompleteAction({ newNotification })
            )
          );
      })
    )
  );

  /**
   * Notification update effect
   */
  public notificationUpdateEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationStoreAction.NOTIFICATION_UPDATE_NOTIFICATION),
      switchMap((action: any) => {
        return this._notificationService
          .getNotificationByID(action.idNotification)
          .pipe(
            map((updatedNotification: Notific) =>
              notificationUpdateCompleteAction({ updatedNotification })
            )
          );
      })
    )
  );

  /**
   * Notification delete effect
   */
  public notificationDeleteEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationStoreAction.NOTIFICATION_DELETE_NOTIFICATION),
      map((action: any) =>
        notificationDeleteCompleteAction({
          idNotification: action.idNotification,
        })
      )
    )
  );
}
